<template lang="pug">
div
  div.order-form(v-if="orderFormData")
    order-sidebar(
      :visible.sync="showSidebar"
      :filter.sync="filter"
      :product-lines="filteredProductLines"
      :selected-line.sync="selectedProductLine"
    )
    product-inspector(
      :product="activeProduct"
    )
    summary-bar(
      v-show="!loading"
      :has-error="formHasError"
      :loading="calculatingOrderSummary"
      :order-summary="orderSummary"
      @show-order-summary="startOrderFlow"
    )
    order-step(:current-step="1")
    h2 Neue Bestellung

    // Banner slider at top of order form
    div.order-form-banner.mb-4
      banner-slider(
        location="order"
        alt="Order Form Banner"
        container-class="order-banner"
        @banner-click="handleOrderBannerClick"
      )

    // Special offers from customer data
    template(v-for="(so, sx) in customer.specialOffersList")
      special-offer(:productQuantities="productQuantities" :specialOffer="so")

    // Order form table with new v2 structure
    table.table.fixed-layout.bottom-margin-for-summarybar(ref="orderFormTable" @click="handleTableClick")
      tbody
        template(v-for="(category, categoryIndex) in orderFormData.categoriesList")
          tr(:key="'cat-' + categoryIndex")
            td.sticky-top.sticky-level-1.sticky-orderform.bg-dark.text-white.main-category(
              colspan="6"
              @click.stop="toggleCategory(categoryIndex)"
            )
              div.d-flex.align-items-center
                svg.toggle-btn.mr-3(viewBox="0 0 24 24" :class="{ open: visibleCategories[categoryIndex] }")
                  path(fill="white" d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M7,10L12,15L17,10H7Z")
                span.category-title {{ category.title }}
                div.flex-fill
                div.search-helper-input
                  input.form-control.form-control-md(
                    type="text"
                    v-model="filter"
                    @click.stop="showSidebar = true"
                    @focus="showSidebar = true"
                    placeholder="Suche..."
                  )
                  svg.search-icon(
                    viewBox="0 0 24 24"
                    @click.stop="showSidebar = true"
                  )
                    path(fill="white" d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z")

          template(v-if="visibleCategories[categoryIndex]")
            tr(:key="'content-' + categoryIndex")
              td.category-content(colspan="6" :data-category-index="categoryIndex")
                div(v-for="(subCategoryGroups, subCategoryName) in getGroupedProductGroups(category)" :key="subCategoryName")
                  div.sub-category-heading(v-if="subCategoryName !== 'default'") {{ subCategoryName }}
                  div.product-groups-flex-container
                    div.product-group-wrapper(
                      v-for="(productGroup, groupIndex) in subCategoryGroups"
                      :key="'grp-' + categoryIndex + '-' + subCategoryName + '-' + groupIndex"
                      :class="{ 'single-column': isSingleColumn(productGroup), 'wide-table': isWideTable(productGroup), 'full-width-table': isFullWidthTable(productGroup) }"
                    )
                      div.product-group-header
                        div.product-name-line
                          span.product-code {{ productGroup.code }}
                          span.product-name {{ productGroup.title }}

                      div.table-wrapper
                        table.product-table
                          thead(v-if="productGroup.columnsDescription && productGroup.columnValuesList && productGroup.columnValuesList.length > 0")
                            tr
                              th.variant-label {{ productGroup.columnsDescription }}
                              th.variant-col(v-for="col in productGroup.columnValuesList" :key="col") {{ col }}
                              th.sum-col Σ
                          tbody
                            template(v-for="(row, rowIndex) in productGroup.rowsList")
                              tr(:key="'row-' + rowIndex")
                                td.row-name
                                  span {{ row.name }}
                                  span.row-price {{ getRowPrice(row) }}
                                td.quantity-cell(v-for="col in productGroup.columnValuesList" :key="col" @click="handleCellClick(productGroup, row, col)")
                                  template(v-if="isAvailable(row, col)")
                                    input.quantity-input(
                                      type="number"
                                      inputmode="numeric"
                                      min="0"
                                      max="200"
                                      :value="getQuantity(productGroup, row, col)"
                                      @input="updateQuantity(productGroup, row, col, $event)"
                                      @focus="selectProduct(productGroup, row, col)"
                                      @click.stop
                                      :class="{ 'validate-minimum-quantity': hasMinimumQuantityError(productGroup, row, col) }"
                                    )
                                    div.minimum-quantity(v-if="hasMinimumQuantityError(productGroup, row, col)")
                                      | {{ getMinimumOrderQuantity(row, col) }}
                                  span.not-available(
                                    v-else-if="hasRealUnavailableReason(row, col)"
                                    :title="getUnavailableReason(row, col)"
                                  ) ✗
                                  div.price-variant-disabled(v-else)
                                td.sum-cell {{ getRowSum(productGroup, row) }}

  // Loading state
  div.bg-dark.text-white(v-else-if="loading")
    h4.py-4.px-4
      span.mr-4.no-underline Lade Daten...
      div.spinner-border.spinner-border-lg.text-success

  // No data state
  div.bg-dark.text-white(v-else)
    h4.py-4.px-4
      div.spinner-border.spinner-border-lg.text-warning
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import OrderSidebar from '@/components/orderForm/orderSidebar'
import ProductInspector from '@/components/orderForm/productInspector'
import SummaryBar from '@/components/orderForm/summaryBar'
import OrderStep from '@/components/orderForm/orderStep'
import OrderFormLegend from '@/components/orderForm/orderFormLegend'
import BannerSlider from '@/components/common/BannerSlider'
import SpecialOffer from '@/components/orderForm/specialOffer'
import get from 'lodash.get'

export default {
  name: 'NewOrder',
  components: {
    OrderSidebar,
    ProductInspector,
    SummaryBar,
    OrderStep,
    OrderFormLegend,
    BannerSlider,
    SpecialOffer
  },
  data () {
    return {
      activeProduct: null,
      calculatingOrderSummary: true,
      filter: '',
      filteredProductLines: [],
      selectedProductLine: null,
      showSidebar: false,
      visibleCategories: {},
      orderFormData: null,
      screenSize: 2,
      resizeTimeout: null
    }
  },
  computed: {
    ...mapState({
      orderSummary: state => get(state, 'orderFormService.latestRequests.getOrderSummary.response.orderSummary', undefined),
      loading: state => state.orderFormService.loadingIndicators.getOrderFormV2Grouped
    }),
    ...mapState('orderForm', [
      'productQuantities'
    ]),
    ...mapGetters('orderForm', [
      'formHasError',
      'orderedProducts'
    ]),
    ...mapGetters('authentication', [
      'customer'
    ])
  },
  watch: {
    customer: {
      handler (newCustomer) {
        // Set up Matomo tracking when customer data becomes available
        if (newCustomer) {
          this.setupMatomoTracking()
        }
      },
      immediate: true
    },
    filter () {
      if (this.filter && this.filter !== '') {
        this.showSidebar = true
      }
      this.refreshFilteredProductLines()
    },
    productQuantities: {
      handler () {
        if (!this.formHasError) {
          this.calculatingOrderSummary = true
          this.refreshOrderSummary()
        }
      },
      deep: true,
      immediate: false
    },
    selectedProductLine: {
      handler (newVal) {
        if (newVal) {
          this.scrollToProductGroup(newVal)
        }
      },
      immediate: false
    },
    '$route.params.focusModelNumber': {
      handler (newFocusModelNumber, oldFocusModelNumber) {
        // Handle focus model number changes from route params
        if (newFocusModelNumber && newFocusModelNumber !== oldFocusModelNumber) {
          this.$nextTick(() => {
            this.focusProductByModelNumber(newFocusModelNumber)
          })
        }
      },
      immediate: false
    }
  },
  async mounted () {
    // Calculate screen size before loading to avoid double load
    this.calculateScreenSize()
    await this.loadOrderFormV2()
    this.refreshOrderSummary()

    // Set up Matomo tracking will happen after customer data is loaded

    // Check if we need to focus on a specific product from route params
    if (this.$route.params.focusModelNumber) {
      this.$nextTick(() => {
        this.focusProductByModelNumber(this.$route.params.focusModelNumber)
      })
    } else {
      this.focusFirstCell()
    }

    window.addEventListener('resize', this.debouncedCalculateScreenSize)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.debouncedCalculateScreenSize)
  },
  methods: {
    ...mapActions('ui', [
      'startOrderFlow'
    ]),
    ...mapActions('orderForm', [
      'updateOrderSummary'
    ]),
    ...mapActions('orderFormService', [
      'getOrderFormV2Grouped'
    ]),
    ...mapMutations('orderForm', [
      'setProductQuantity'
    ]),

    async loadOrderFormV2 () {
      try {
        const requestPayload = {
          order_form: 'default',
          screenSize: this.screenSize
        }
        await this.getOrderFormV2Grouped(requestPayload)

        // Get the response data from state regardless of success (might be cached)
        const response = get(this.$store.state, 'orderFormService.latestRequests.getOrderFormV2Grouped.response', null)

        if (response) {
          this.orderFormData = response

          // Initialize visible categories (only first category open by default)
          if (this.orderFormData && this.orderFormData.categoriesList) {
            this.orderFormData.categoriesList.forEach((_, index) => {
              this.$set(this.visibleCategories, index, index === 0)
            })
          }
        }
      } catch (error) {
        console.error('Error loading order form V2:', error)
      }
    },

    toggleCategory (index) {
      this.$set(this.visibleCategories, index, !this.visibleCategories[index])
    },

    getVisibleProductGroups (category) {
      if (!category || !category.productGroupsList) return []

      // Always return all product groups - no filtering
      return category.productGroupsList
    },

    getGroupedProductGroups (category) {
      if (!category) {
        return { 'default': [] }
      }

      const productGroups = this.getVisibleProductGroups(category)
      const grouped = {}

      productGroups.forEach(pg => {
        // Use backend-provided sub-category from database
        let subCategory = pg.subCategory || 'default'

        if (!grouped[subCategory]) {
          grouped[subCategory] = []
        }
        grouped[subCategory].push(pg)
      })

      // Preserve backend ordering instead of sorting alphabetically
      const sortedGroups = {}
      const seenSubCategories = new Set()

      // First pass: collect subcategories in the order they appear (backend order)
      productGroups.forEach(pg => {
        const subCategory = pg.subCategory || 'default'
        if (subCategory !== 'default' && !seenSubCategories.has(subCategory)) {
          seenSubCategories.add(subCategory)
        }
      })

      // Convert Set to array to preserve insertion order
      const order = Array.from(seenSubCategories)

      // Always pin "Sonderpreis" subcategory to the top
      const sonderpreisIndex = order.indexOf('Sonderpreis')
      if (sonderpreisIndex > 0) {
        order.splice(sonderpreisIndex, 1)
        order.unshift('Sonderpreis')
      }

      // Special handling for Angebote category (if needed)
      if (category.title === 'Angebote') {
        // If backend doesn't provide the expected order, we can override
        // But ideally, backend should handle this via order_form_order
        const hasSpecificOrder = order.includes('Make-up') || order.includes('Pflege')
        if (hasSpecificOrder) {
          // Use backend order
        } else {
          // Fallback if needed
          order.length = 0
          order.push('Make-up', 'Pflege')
        }
      }

      // Add ordered sub-categories
      order.forEach(cat => {
        if (grouped[cat] && grouped[cat].length > 0) {
          sortedGroups[cat] = grouped[cat]
        }
      })

      // Add default group without sub-category heading (always last)
      if (grouped['default'] && grouped['default'].length > 0) {
        sortedGroups['default'] = grouped['default']
      }

      return sortedGroups
    },

    hasAvailableProducts (productGroup) {
      if (!productGroup || !productGroup.rowsList) return false
      return productGroup.rowsList.some(row =>
        row.availableColumnsList && row.availableColumnsList.length > 0
      )
    },

    isSingleColumn (productGroup) {
      // Check if product group has only 1 column (no variants)
      return productGroup && productGroup.columnValuesList && productGroup.columnValuesList.length <= 1
    },

    isWideTable (productGroup) {
      // Check if product group has many columns and should span 2 grid columns (for screen size 2)
      return this.screenSize === 2 && productGroup && productGroup.columnValuesList && productGroup.columnValuesList.length > 8
    },

    isFullWidthTable (productGroup) {
      // Check if product group has many columns and should span full width (for screen size 3)
      return this.screenSize === 3 && productGroup && productGroup.columnValuesList && productGroup.columnValuesList.length > 8
    },

    isAvailable (row, column) {
      return row.availableColumnsList && row.availableColumnsList.includes(column)
    },

    getUnavailableReason (row, column) {
      // Handle array format (from Vue reactive conversion)
      if (row.unavailableReasonsMap && Array.isArray(row.unavailableReasonsMap)) {
        for (let i = 0; i < row.unavailableReasonsMap.length; i++) {
          const entry = row.unavailableReasonsMap[i]
          if (Array.isArray(entry) && entry.length >= 2 && entry[0] === column) {
            return entry[1]
          }
        }
      }

      // Handle protobuf Map format
      if (row.unavailableReasonsMap && row.unavailableReasonsMap.get) {
        const reason = row.unavailableReasonsMap.get(column)
        if (reason) return reason
      }

      // Fallback to old format
      return (row.unavailableReasons && row.unavailableReasons[column]) || 'Nicht verfügbar'
    },

    hasRealUnavailableReason (row, column) {
      // Check if there's a real unavailable reason (not just missing/different price)
      const reason = this.getUnavailableReason(row, column)
      // Only show red X for actual availability issues
      return reason && reason !== 'Nicht verfügbar' && reason !== 'Different price variant'
    },

    getMinimumOrderQuantity (row, column) {
      // Get minimum order quantity from backend data
      if (row.minimumOrderQuantitiesMap) {
        // Handle array format (from Vue reactive conversion)
        if (Array.isArray(row.minimumOrderQuantitiesMap)) {
          for (let i = 0; i < row.minimumOrderQuantitiesMap.length; i++) {
            const entry = row.minimumOrderQuantitiesMap[i]
            if (Array.isArray(entry) && entry.length >= 2 && entry[0] === column) {
              return entry[1]
            }
          }
        } else if (row.minimumOrderQuantitiesMap.get && row.minimumOrderQuantitiesMap.get(column)) {
          // Handle protobuf Map format
          return row.minimumOrderQuantitiesMap.get(column)
        }
      }
      return 0
    },

    hasMinimumQuantityError (productGroup, row, column) {
      const quantity = this.getQuantity(productGroup, row, column)
      const minimumQuantity = this.getMinimumOrderQuantity(row, column)

      // Show error if quantity > 0 but less than minimum required
      return quantity > 0 && minimumQuantity > 0 && quantity < minimumQuantity
    },

    getProductKey (productGroup, row, column) {
      // Use actual product ID from backend instead of custom key

      // Handle array format (from Vue reactive conversion)
      if (row.productIdsMap && Array.isArray(row.productIdsMap)) {
        for (let i = 0; i < row.productIdsMap.length; i++) {
          const entry = row.productIdsMap[i]
          if (Array.isArray(entry) && entry.length >= 2 && entry[0] === column) {
            return entry[1] // Return the product ID
          }
        }
      }

      // Handle protobuf Map format
      if (row.productIdsMap && row.productIdsMap.get && row.productIdsMap.get(column)) {
        return row.productIdsMap.get(column)
      }

      // Fallback to custom key if product ID not available
      return `${productGroup.code}_${row.name}_${column}`
    },

    getQuantity (productGroup, row, column) {
      const key = this.getProductKey(productGroup, row, column)
      return this.productQuantities[key] || ''
    },

    updateQuantity (productGroup, row, column, event) {
      let value = parseInt(event.target.value) || null

      // Enforce maximum quantity of 200
      if (value !== null && value > 200) {
        value = 200
        // Update the input field to show the capped value
        event.target.value = '200'
      }

      const key = this.getProductKey(productGroup, row, column)
      this.setProductQuantity({ productId: key, quantity: value })
    },

    getRowPrice (row) {
      const isStudent = this.customer && this.customer.studentProducts === 1
      const priceString = isStudent && row.priceB2c ? row.priceB2c : row.priceB2b
      return priceString || ''
    },

    getRowSum (productGroup, row) {
      let sum = 0
      if (productGroup && productGroup.columnValuesList) {
        productGroup.columnValuesList.forEach(column => {
          const key = this.getProductKey(productGroup, row, column)
          const quantity = this.productQuantities[key]
          if (quantity && quantity > 0) {
            sum += quantity
          }
        })
      }

      // Always show price calculation, even when sum is 0
      // For students use B2C price, for others use B2B price
      const isStudent = this.customer && this.customer.studentProducts === 1

      const priceString = isStudent && row.priceB2c ? row.priceB2c : row.priceB2b

      if (priceString) {
        const price = parseFloat(priceString.replace('€', '').replace(',', '.').trim())
        if (!isNaN(price)) {
          const total = (sum * price).toFixed(2).replace('.', ',')
          return `${total} €`
        }
      }

      return '0,00 €'
    },

    selectProduct (productGroup, row, column) {
      // Get the actual product ID from the backend
      let productId = null

      // Handle array format (from Vue reactive conversion)
      if (row.productIdsMap && Array.isArray(row.productIdsMap)) {
        for (let i = 0; i < row.productIdsMap.length; i++) {
          const entry = row.productIdsMap[i]
          if (Array.isArray(entry) && entry.length >= 2 && entry[0] === column) {
            productId = entry[1]
            break
          }
        }
      }

      // Handle protobuf Map format
      if (!productId && row.productIdsMap && row.productIdsMap.get && row.productIdsMap.get(column)) {
        productId = row.productIdsMap.get(column)
      }

      // Get variant name from backend data
      let variantName = ''

      // Handle array format (from Vue reactive conversion)
      if (row.variantNamesMap && Array.isArray(row.variantNamesMap)) {
        for (let i = 0; i < row.variantNamesMap.length; i++) {
          const entry = row.variantNamesMap[i]
          if (Array.isArray(entry) && entry.length >= 2 && entry[0] === column) {
            variantName = entry[1]
            break
          }
        }
      }

      // Handle protobuf Map format
      if (!variantName && row.variantNamesMap && row.variantNamesMap.get && row.variantNamesMap.get(column)) {
        variantName = row.variantNamesMap.get(column)
      }

      // Fallback to constructed name if not available (for backwards compatibility)
      if (!variantName) {
        let variantParts = []
        if (row.name !== 'Menge') {
          variantParts.push(row.name)
        }
        if (productGroup.columnsDescription && productGroup.columnsDescription.trim() !== '') {
          variantParts.push(column)
        }
        variantName = variantParts.length > 0 ? variantParts.join(' - ') : ''
      }

      // Get the actual data from the backend
      let actualModelNumber = productGroup.code // fallback to product line code
      let actualIngredients = ''
      let actualDescription = ''

      // Try to get the real data from the backend
      if (row.modelNumbersMap) {
        // Handle array format (from Vue reactive conversion)
        if (Array.isArray(row.modelNumbersMap)) {
          for (let i = 0; i < row.modelNumbersMap.length; i++) {
            const entry = row.modelNumbersMap[i]
            if (Array.isArray(entry) && entry.length >= 2 && entry[0] === column) {
              actualModelNumber = entry[1]
              break
            }
          }
        } else if (row.modelNumbersMap.get && row.modelNumbersMap.get(column)) {
          // Handle protobuf Map format
          actualModelNumber = row.modelNumbersMap.get(column)
        }
      }

      // Get ingredients
      if (row.ingredientsMap) {
        if (Array.isArray(row.ingredientsMap)) {
          for (let i = 0; i < row.ingredientsMap.length; i++) {
            const entry = row.ingredientsMap[i]
            if (Array.isArray(entry) && entry.length >= 2 && entry[0] === column) {
              actualIngredients = entry[1]
              break
            }
          }
        } else if (row.ingredientsMap.get && row.ingredientsMap.get(column)) {
          actualIngredients = row.ingredientsMap.get(column)
        }
      }

      // Get description
      if (row.descriptionsMap) {
        if (Array.isArray(row.descriptionsMap)) {
          for (let i = 0; i < row.descriptionsMap.length; i++) {
            const entry = row.descriptionsMap[i]
            if (Array.isArray(entry) && entry.length >= 2 && entry[0] === column) {
              actualDescription = entry[1]
              break
            }
          }
        } else if (row.descriptionsMap.get && row.descriptionsMap.get(column)) {
          actualDescription = row.descriptionsMap.get(column)
        }
      }

      if (productId) {
        // Create product object with ID for inspector (same format as original)
        this.activeProduct = {
          id: productId,
          modelNumber: actualModelNumber,
          nameDe: productGroup.title,
          variantName: variantName,
          description: actualDescription,
          ingredients: actualIngredients,
          quantityPerPack: 0,
          quantityUnitName: ''
        }
      } else {
        // Fallback for mock product
        this.activeProduct = {
          modelNumber: actualModelNumber,
          nameDe: productGroup.title,
          variantName: variantName,
          description: actualDescription,
          ingredients: actualIngredients,
          quantityPerPack: 0,
          quantityUnitName: ''
        }
      }
    },

    closeProductInspector () {
      this.activeProduct = null
    },

    handleTableClick (event) {
      // Close sidebar when clicking anywhere on the table
      this.showSidebar = false

      // Close product inspector unless clicking on a cell or input
      const target = event.target
      const isCell = target.closest('.quantity-cell')
      const isInput = target.matches('.quantity-input')

      if (!isCell && !isInput) {
        this.closeProductInspector()
      }
    },

    handleCellClick (productGroup, row, column) {
      // Only select product if clicking on an available cell
      if (this.isAvailable(row, column)) {
        this.selectProduct(productGroup, row, column)
      } else {
        // Close inspector when clicking on unavailable cell
        this.closeProductInspector()
      }
    },

    refreshFilteredProductLines () {
      let res = []
      if (this.filter === '') {
        this.selectedProductLine = null
      }
      if (this.filter && this.orderFormData && this.orderFormData.categoriesList) {
        this.orderFormData.categoriesList.forEach((category, categoryIndex) => {
          // Get the actual visible product groups (same logic as template)
          const groupedProductGroups = this.getGroupedProductGroups(category)
          let visibleGroupIndex = 0

          Object.values(groupedProductGroups).forEach(subCategoryGroups => {
            subCategoryGroups.forEach((productGroup) => {
              const searchTerm = this.filter.toLowerCase().trim()
              const searchTermClean = searchTerm.replace(/\./g, '') // Also search without dots

              // Split search into words for multi-term matching
              const searchWords = searchTerm.split(/\s+/).filter(w => w.length > 0)
              const isMultiWordSearch = searchWords.length > 1

              // Check if searching for the group code (e.g., "419.XX" or "419")
              const groupCodeMatch = (
                productGroup.code && (
                  productGroup.code.toLowerCase() === searchTerm ||
                  productGroup.code.toLowerCase().replace('.xx', '') === searchTerm ||
                  productGroup.code.toLowerCase().replace(/\./g, '') === searchTermClean
                )
              )

              // Now search through individual variants/products in each row and column
              if (productGroup.rowsList) {
                productGroup.rowsList.forEach((row, rowIndex) => {
                  // Check each column (variant)
                  if (productGroup.columnValuesList) {
                    productGroup.columnValuesList.forEach(column => {
                      let variantMatch = false
                      let variantName = ''
                      let modelNumber = ''

                      // Get model number for this cell - handle both Map formats
                      // Handle array format (from Vue reactive conversion)
                      if (row.modelNumbersMap && Array.isArray(row.modelNumbersMap)) {
                        for (let i = 0; i < row.modelNumbersMap.length; i++) {
                          const entry = row.modelNumbersMap[i]
                          if (Array.isArray(entry) && entry.length >= 2 && entry[0] === column) {
                            modelNumber = entry[1]
                            break
                          }
                        }
                      }
                      // Handle protobuf Map format
                      if (!modelNumber && row.modelNumbersMap && row.modelNumbersMap.get && row.modelNumbersMap.get(column)) {
                        modelNumber = row.modelNumbersMap.get(column)
                      }

                      // Get variant name for this cell - handle both Map formats
                      // Handle array format (from Vue reactive conversion)
                      if (row.variantNamesMap && Array.isArray(row.variantNamesMap)) {
                        for (let i = 0; i < row.variantNamesMap.length; i++) {
                          const entry = row.variantNamesMap[i]
                          if (Array.isArray(entry) && entry.length >= 2 && entry[0] === column) {
                            variantName = entry[1]
                            break
                          }
                        }
                      }
                      // Handle protobuf Map format
                      if (!variantName && row.variantNamesMap && row.variantNamesMap.get && row.variantNamesMap.get(column)) {
                        variantName = row.variantNamesMap.get(column)
                      }

                      // If searching for group code, include all variants
                      if (groupCodeMatch) {
                        variantMatch = true
                      } else if (isMultiWordSearch) {
                        // Multi-word search: ALL words must be found somewhere in the product info
                        const combinedText = [
                          modelNumber || '',
                          productGroup.title || '',
                          variantName || '',
                          row.name || ''
                        ].join(' ').toLowerCase()

                        // Check if ALL search words are present
                        variantMatch = searchWords.every(word => combinedText.includes(word))
                      } else {
                        // Single word/term search with existing logic
                        // Search in model number with special rules
                        if (modelNumber) {
                          const modelLower = modelNumber.toLowerCase()
                          const modelClean = modelLower.replace(/\./g, '')

                          // Check if search term looks like a product number (starts with digits or MW prefix)
                          const isNumberSearch = /^(\d|mw\d)/i.test(searchTerm)

                          if (isNumberSearch) {
                            // For number searches, allow prefix matching (like SQL LIKE 'search%')
                            // "794" should match "7943", but still use word boundaries to prevent "7943" matching "77943"
                            const searchRegex = new RegExp(`\\b${searchTerm}`, 'i') // Match at word boundary start
                            const searchCleanRegex = new RegExp(`\\b${searchTermClean}`, 'i')

                            if (searchRegex.test(modelNumber) || searchCleanRegex.test(modelClean)) {
                              variantMatch = true
                            }

                            // Special MW prefix rules
                            if (!variantMatch) {
                              // If searching for "2511", also match "MW2511"
                              if (/^\d/.test(searchTerm)) {
                                const mwSearch = 'mw' + searchTerm
                                const mwSearchClean = 'mw' + searchTermClean
                                // Use prefix matching for MW variants too
                                if (modelLower.startsWith(mwSearch) || modelClean.startsWith(mwSearchClean)) {
                                  variantMatch = true
                                }
                              }

                              // If searching for "MW2511", also match "2511"
                              if (/^mw\d/i.test(searchTerm)) {
                                const withoutMW = searchTerm.replace(/^mw/i, '')
                                const withoutMWClean = searchTermClean.replace(/^mw/i, '')
                                // Use prefix matching here too
                                const searchRegexNoMW = new RegExp(`\\b${withoutMW}`, 'i')
                                const searchCleanRegexNoMW = new RegExp(`\\b${withoutMWClean}`, 'i')

                                if (searchRegexNoMW.test(modelNumber) || searchCleanRegexNoMW.test(modelClean)) {
                                  variantMatch = true
                                }
                              }
                            }
                          } else {
                            // For non-number searches (text), allow partial matches
                            if (modelLower.includes(searchTerm) || modelClean.includes(searchTermClean)) {
                              variantMatch = true
                            }
                          }
                        }

                        // Search in variant name (e.g., "Havanna")
                        if (!variantMatch && variantName && variantName.toLowerCase().includes(searchTerm)) {
                          variantMatch = true
                        }

                        // Search in product title
                        if (!variantMatch && productGroup.title && productGroup.title.toLowerCase().includes(searchTerm)) {
                          variantMatch = true
                        }

                        // Search in row name (e.g., "Tester", "Menge")
                        if (!variantMatch && row.name && row.name.toLowerCase().includes(searchTerm)) {
                          variantMatch = true
                        }
                      }

                      if (variantMatch) {
                        // Out-of-stock variants are surfaced in search so the
                        // customer can find them even when no quantity input
                        // is rendered; the result row tags them as such.
                        const isOrderable = row.availableColumnsList && row.availableColumnsList.includes(column)

                        // Build a proper search result title
                        let parts = []

                        // Add product title (which already includes the model number from backend)
                        if (productGroup.title) {
                          parts.push(productGroup.title)
                        }

                        // Add variant name if available
                        if (variantName) {
                          parts.push(variantName)
                        }

                        // Only add row name if it's not the default "Menge"
                        if (row.name && row.name !== 'Menge') {
                          parts.push(`(${row.name})`)
                        }

                        if (!isOrderable) {
                          parts.push('— Nicht auf Lager')
                        }

                        const variantTitle = parts.join(' ')

                        res.push({
                          title: variantTitle,
                          modelNumberPattern: modelNumber || productGroup.code,
                          productGroup: category.title,
                          productVariant: variantName || column,
                          href: `#product-${categoryIndex}-${visibleGroupIndex}`,
                          categoryTitle: category.title,
                          categoryIndex: categoryIndex,
                          groupIndex: visibleGroupIndex,
                          productCode: modelNumber || productGroup.code,
                          matchedColumn: column,
                          rowName: row.name,
                          isOrderable: isOrderable,
                          productGroupData: productGroup
                        })
                      }
                    })
                  }
                })
              }
              visibleGroupIndex++
            })
          })
        })
      }
      this.filteredProductLines = res
    },

    refreshOrderSummary () {
      this.updateOrderSummary().then(() => {
        this.calculatingOrderSummary = false
      })
    },

    calculateScreenSize () {
      const width = window.innerWidth
      const oldScreenSize = this.screenSize

      // Screen size mapping to match backend expectations
      if (width < 768) {
        // Mobile (< 768px)
        this.screenSize = 1
      } else if (width < 1200) {
        // Tablet/Desktop (768px - 1200px)
        this.screenSize = 2
      } else {
        // Large Desktop (> 1200px)
        this.screenSize = 3
      }

      // If screen size changed, reload data to re-split tables
      if (oldScreenSize !== this.screenSize && this.orderFormData) {
        this.loadOrderFormV2()
      }
    },

    debouncedCalculateScreenSize () {
      // Debounce resize events to avoid excessive API calls
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout)
      }
      this.resizeTimeout = setTimeout(() => {
        this.calculateScreenSize()
      }, 300)
    },

    focusFirstCell () {
      // Use nextTick to ensure DOM is fully rendered
      this.$nextTick(() => {
        // Find the first available quantity input in the first visible category
        const firstInput = this.$el.querySelector('.quantity-input')
        if (firstInput) {
          firstInput.focus()
        }
      })
    },

    handleOrderBannerClick (clickData) {
      // Track banner click with Matomo if available
      this.trackBannerClick(clickData)
      this.focusProductByModelNumber(clickData.modelNumber)
    },

    setupMatomoTracking () {
      // Check if Matomo is available (from parent page) and customer data exists
      if (typeof window._paq !== 'undefined' && window._paq && this.customer) {
        // Only use SAP ID - no fallback
        const sapId = this.customer.sapId

        // Only set dimension if SAP ID is available and not already set
        if (sapId && !this.matomoDimensionSet) {
          // Set custom dimension for SAP ID globally
          try {
            window._paq.push(['setCustomDimension', 1, sapId])
            this.matomoDimensionSet = true
          } catch (error) {
            console.error('Error setting Matomo SAP ID dimension:', error)
          }
        }
      }
    },

    trackBannerClick (clickData) {
      // Check if Matomo is available (from parent page)
      if (typeof window._paq !== 'undefined' && window._paq) {
        const bannerName = clickData.modelNumber || clickData.name || 'banner'

        // Track the banner click event
        // SAP ID dimension is already set globally if available
        try {
          window._paq.push([
            'trackEvent',
            'b2b_order_form',
            'banner_click',
            bannerName
          ])
        } catch (error) {
          console.error('Error tracking banner click:', error)
        }
      }
    },

    focusProductByModelNumber (modelNumber) {
      if (!this.orderFormData || !this.orderFormData.categoriesList) return

      // Search all categories for the individual product cell with matching model number
      for (let categoryIndex = 0; categoryIndex < this.orderFormData.categoriesList.length; categoryIndex++) {
        const category = this.orderFormData.categoriesList[categoryIndex]

        // Get all product groups in this category
        const groupedProductGroups = this.getGroupedProductGroups(category)
        let groupIndex = 0

        for (const subCategoryGroups of Object.values(groupedProductGroups)) {
          for (const productGroup of subCategoryGroups) {
            // Search within this product group's rows and columns for the model number
            if (productGroup.rowsList && productGroup.columnValuesList) {
              for (const row of productGroup.rowsList) {
                for (const column of productGroup.columnValuesList) {
                  // Get model number for this specific cell
                  let cellModelNumber = null

                  if (row.modelNumbersMap) {
                    // Handle array format (from Vue reactive conversion)
                    if (Array.isArray(row.modelNumbersMap)) {
                      for (let i = 0; i < row.modelNumbersMap.length; i++) {
                        const entry = row.modelNumbersMap[i]
                        if (Array.isArray(entry) && entry.length >= 2 && entry[0] === column) {
                          cellModelNumber = entry[1]
                          break
                        }
                      }
                    } else if (row.modelNumbersMap.get && row.modelNumbersMap.get(column)) {
                      // Handle protobuf Map format
                      cellModelNumber = row.modelNumbersMap.get(column)
                    }
                  }

                  if (cellModelNumber === modelNumber) {
                    // Found it! Make sure category is visible
                    this.$set(this.visibleCategories, categoryIndex, true)

                    this.$nextTick(() => {
                      // Scroll to this product group
                      this.scrollToProductGroup({
                        categoryIndex: categoryIndex,
                        groupIndex: groupIndex,
                        categoryTitle: category.title,
                        productCode: productGroup.code
                      })

                      // Also try to focus the specific cell after scrolling
                      setTimeout(() => {
                        this.selectProduct(productGroup, row, column)
                      }, 1000)
                    })
                    return
                  }
                }
              }
            }
            groupIndex++
          }
        }
      }
    },

    focusSpecificCell (productGroupElement, productGroup, targetColumn, targetRowName) {
      // Find the column index
      let columnIndex = -1
      if (productGroup.columnValuesList) {
        columnIndex = productGroup.columnValuesList.indexOf(targetColumn)
      }

      if (columnIndex === -1) {
        return
      }

      // Find all rows in this product group
      const rows = productGroupElement.querySelectorAll('tbody tr')

      // Look for the specific row by row name, or first available if no row name specified
      for (let row of rows) {
        // Check if this is the target row (if rowName is specified)
        if (targetRowName) {
          const rowNameCell = row.querySelector('.row-name')
          if (rowNameCell) {
            const rowText = rowNameCell.textContent.trim()
            // Skip if this is not the target row
            if (!rowText.includes(targetRowName)) {
              continue
            }
          }
        }

        const cells = row.querySelectorAll('.quantity-cell')
        if (cells[columnIndex]) {
          const input = cells[columnIndex].querySelector('.quantity-input')
          if (input) {
            input.focus()
            input.select()

            // Also highlight the cell temporarily
            cells[columnIndex].classList.add('search-highlight-cell')
            setTimeout(() => {
              cells[columnIndex].classList.remove('search-highlight-cell')
            }, 3000)

            return
          }
        }
      }
    },

    scrollToProductGroup (selectedLine) {
      this.$nextTick(() => {
        // First ensure the category is visible
        if (selectedLine.categoryIndex !== undefined) {
          this.$set(this.visibleCategories, selectedLine.categoryIndex, true)

          this.$nextTick(() => {
            // Find the product group element using a more reliable selector
            const categoryEl = this.$el.querySelector(`[data-category-index="${selectedLine.categoryIndex}"]`)

            if (categoryEl) {
              const productGroups = categoryEl.querySelectorAll('.product-group-wrapper')

              if (productGroups[selectedLine.groupIndex]) {
                const targetElement = productGroups[selectedLine.groupIndex]

                // Add highlight class
                targetElement.classList.add('search-highlight')
                setTimeout(() => {
                  targetElement.classList.remove('search-highlight')
                }, 3000)

                // Scroll to element
                targetElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                })

                // If we have a specific column and row match, focus that cell
                if (selectedLine.matchedColumn && selectedLine.productGroupData) {
                  setTimeout(() => {
                    this.focusSpecificCell(targetElement, selectedLine.productGroupData, selectedLine.matchedColumn, selectedLine.rowName)
                  }, 800)
                } else {
                  // Otherwise focus first available input in the product group
                  const firstInput = targetElement.querySelector('.quantity-input')
                  if (firstInput) {
                    setTimeout(() => {
                      firstInput.focus()
                      firstInput.select()
                    }, 800)
                  }
                }
              } else {
                // Product group not found at index
              }
            } else {
              // Category element not found for index
            }
          })
        } else {
          // No categoryIndex in selectedLine
        }
      })
    }

  }
}
</script>

<style lang="stylus" scoped>
.no-underline
  text-decoration: none !important

.toggle-btn
  width: 24px
  height: 24px
  transition: transform 0.3s ease
  cursor: pointer
  flex-shrink: 0

  &.open
    transform: rotate(180deg)

.sticky-level-1
  z-index: 995

.sticky-orderform
  top: 65px !important

.main-category
  cursor: pointer

  .category-title
    font-size: 1.25rem
    font-weight: 500

.search-helper-input
  display: flex
  align-items: center
  position: relative
  max-width: 300px

  input
    padding-right: 35px
    background: rgba(255, 255, 255, 0.1)
    border: 1px solid rgba(255, 255, 255, 0.2)
    color: white

    &::placeholder
      color: rgba(255, 255, 255, 0.6)

    &:focus
      background: rgba(255, 255, 255, 0.15)
      border-color: rgba(255, 255, 255, 0.4)
      color: white
      box-shadow: none

  .search-icon
    width: 20px
    height: 20px
    position: absolute
    right: 10px
    cursor: pointer
    pointer-events: all

.category-content
  padding: 1rem

.product-groups-flex-container
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr))
  gap: 1rem
  align-items: start
  justify-content: start

.product-group-wrapper
  display: flex
  flex-direction: column
  border: 1px solid rgba(240, 230, 236, 0.2)
  overflow: hidden
  background-color: white
  min-width: 0
  transition: all 0.3s ease

  &.search-highlight
    border: 2px solid #BEADA2
    background-color: rgba(196, 125, 164, 0.1)
    box-shadow: 0 0 10px rgba(196, 125, 164, 0.3)

  &.single-column
    max-width: 400px

  &.wide-table
    grid-column: span 2

  &.full-width-table
    grid-column: 1 / -1

.product-group-header
  background-color: #faf9fb
  border-bottom: 1px solid rgba(240, 230, 236, 0.2)

  .product-name-line
    padding: 0.5rem
    display: flex
    align-items: center
    gap: 0.5rem

    .product-code
      font-weight: bold
      color: #6c757d
      font-size: 0.9em

    .product-name
      font-weight: bold
      font-size: 1.1em
      color: #2c3e50

.table-wrapper
  overflow-x: auto
  display: block
  margin-right: 0
  margin-bottom: 0

.product-table
  width: 100%
  min-width: fit-content
  border-collapse: collapse
  white-space: nowrap

  tbody tr
    height: 36px

  th, td
    border: 1px solid rgba(240, 230, 236, 0.3)
    padding: 4px
    height: 36px
    vertical-align: middle
    text-align: center

  .variant-label
    background-color: #faf9fb
    text-align: left
    padding-left: 8px
    min-width: 100px
    color: #BEADA2
    font-weight: 400

  .variant-col
    background-color: #faf9fb
    min-width: 40px
    color: #BEADA2
    font-weight: bold

  .sum-col
    background-color: #f5f0f3
    font-weight: 500
    min-width: 70px
    width: 70px
    max-width: 70px
    text-align: right
    padding-right: 6px
    color: #BEADA2

  .sum-cell
    background-color: #f5f0f3
    font-weight: 500
    vertical-align: middle
    text-align: right
    padding-right: 6px
    min-width: 70px
    width: 70px
    max-width: 70px
    color: #BEADA2

  .row-name
    background-color: #faf9fb
    text-align: left
    padding-left: 8px
    padding-right: 8px
    font-weight: 400
    height: 36px
    vertical-align: middle
    display: flex
    justify-content: space-between
    align-items: center

    .row-price
      color: #BEADA2
      font-size: 0.9em
      margin-left: auto
      padding-left: 8px
      white-space: nowrap

  .price-row
    .row-name.price-label
      font-size: 0.85em
      font-style: italic
      color: #6c757d

    .price-cell
      font-size: 0.85em
      color: #BEADA2
      background-color: #fdf8fc

  .quantity-cell
    padding: 0
    width: 36px
    height: 36px
    border: 2px solid #BEADA2
    position: relative
    vertical-align: middle

    .quantity-input
      width: 100%
      height: 100%
      border: none
      border-radius: 0
      text-align: center
      padding: 0
      margin: 0
      box-sizing: border-box
      background-color: white

      // Hide native number input arrows
      -moz-appearance: textfield

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button
        -webkit-appearance: none
        margin: 0

      &:focus
        outline: none
        background-color: #fdf8fc

    .not-available
      color: #dc3545
      cursor: help
      display: flex
      align-items: center
      justify-content: center
      height: 100%

    .price-variant-disabled
      width: 100%
      height: 100%
      background-color: #f1f0f2
      border: none

    .validate-minimum-quantity
      border: 2px solid #dc3545 !important

    .minimum-quantity
      display: flex
      position: absolute
      top: 2px
      left: 2px
      width: 16px
      height: 16px
      align-items: center
      justify-content: center
      color: #dc3545
      font-size: 70%
      font-weight: bold
      z-index: 10

.product-line-spacer
  height: 16px

  td
    border: none
    padding: 0

.sub-category-heading
  font-weight: 300
  font-size: 1.3rem
  color: #BEADA2
  margin-bottom: 1rem
  margin-top: 1.5rem
  padding-bottom: 0.5rem
  border-bottom: 1px solid rgba(196, 125, 164, 0.2)
  letter-spacing: 0.5px

.bottom-margin-for-summarybar
  margin-bottom: 180px

.order-form-banner
  max-width: 100%
  margin: 0 auto 1rem auto

  .order-banner
    border-radius: 6px
    overflow: hidden
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1)
    transition: all 0.3s ease

    &:hover
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15)
      transform: translateY(-1px)

// Responsive grid adjustments
@media all and (max-width: 1200px)
  .product-groups-flex-container
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr))

@media all and (max-width: 768px)
  .product-groups-flex-container
    grid-template-columns: 1fr

  .bottom-margin-for-summarybar
    margin-bottom: 180px

@media all and (max-width: 480px)
  .product-groups-flex-container
    gap: 0.5rem

  .category-content
    padding: 0.5rem
</style>
